import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { MultiSelect } from "../receivables/common";
import { tableRows } from "../../utils/xlsx";
import {
  ACCOUNTING,
  cellRight,
  defaultPeriod,
  download,
  EmptyState,
  ExportButton,
  fyStartIso,
  LoadingOverlay,
  money,
  PeriodFilter,
  periodLabel,
  REPORTS,
  sheetHeading,
  StatCard,
  todayIso,
  validPeriod,
} from "./common";

const ENTRY_LIMIT = 5000;

// Expense heads are the ledgers under these two groups; the group decides where the expense lands in the P&L.
const TYPES = [
  { group: "INDIRECT_EXPENSES", label: "Indirect Expenses", hint: "Running costs: salaries, rent, electricity, fuel. Reduce net profit only." },
  { group: "DIRECT_EXPENSES", label: "Direct Expenses", hint: "Costs of getting goods in: inward freight, unloading. Reduce gross profit too." },
];
const EXPENSE_GROUPS = TYPES.map((t) => t.group);
const CASH_BANK_GROUPS = ["CASH_IN_HAND", "BANK_ACCOUNTS", "BANK_OD"];
// Where an unpaid expense is owed: a payable such as Salaries Payable (Provisions) or a vendor such as a garage (Sundry Creditors).
const PAYABLE_GROUPS = ["PROVISIONS", "SUNDRY_CREDITORS"];

const typeLabel = (group) => TYPES.find((t) => t.group === group)?.label || group;
const isPaid = (voucher) => voucher.entries.some((e) => CASH_BANK_GROUPS.includes(e.group) && Number(e.credit) > 0);
const otherSide = (voucher) => [...new Set(voucher.entries.filter((e) => !EXPENSE_GROUPS.includes(e.group)).map((e) => e.ledgerName))].join(", ");
const sum = (rows, key = "amount") => rows.reduce((total, row) => total + Number(row[key] || 0), 0);
const indent = (level) => ({ textAlign: "left", paddingLeft: `${12 + level * 22}px` });
const monthOf = (date) => (date || "").slice(0, 7);
const monthLabel = (month) => {
  if (!month) return "-";
  const [y, m] = month.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
};
const rowButton = { background: "none", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", font: "inherit", color: "inherit" };

/**
 * Expenses for a period: the two expense types, each opening to its heads, each head opening to its vouchers — plus what
 * is still unpaid. Read-only: heads are created in Ledgers and expenses are entered in Vouchers / Day Book.
 */
const ExpenseReport = () => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [applied, setApplied] = useState(null);
  const [heads, setHeads] = useState([]);
  const [entries, setEntries] = useState([]);
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  // filters applied on the loaded period: expense type, ledgers (heads), month; and the view (by head / month-wise)
  const [typeFilter, setTypeFilter] = useState("");
  const [ledgerFilter, setLedgerFilter] = useState([]);
  const [monthFilter, setMonthFilter] = useState("");
  const [view, setView] = useState("head");

  const load = async () => {
    if (!validPeriod(period)) return;
    setLoading(true);
    try {
      // Head totals come from the ledger-wise report the Trial Balance uses, so they always agree with the P&L.
      const headRows = [];
      for (const group of EXPENSE_GROUPS) {
        const report = await api.get(`${REPORTS}/ledger-wise`, { fromDate: period.fromDate, toDate: period.toDate, group });
        headRows.push(...(report?.ledgers || []).map((row) => ({ ...row, amount: Number(row.debit || 0) - Number(row.credit || 0) })));
      }
      const vouchers = await api.get(`${ACCOUNTING}/vouchers`, {
        fromDate: period.fromDate,
        toDate: period.toDate,
        groups: EXPENSE_GROUPS.join(","),
        sort: "voucherDate,asc",
        page: 0,
        size: ENTRY_LIMIT,
      });
      if ((vouchers?.totalElements || 0) > (vouchers?.content || []).length)
        toast.warn(`Only the first ${vouchers.content.length} of ${vouchers.totalElements} expense vouchers are listed — narrow the period for the rest. Totals are complete.`);
      const lines = (vouchers?.content || []).flatMap((voucher) =>
        voucher.entries
          .filter((e) => EXPENSE_GROUPS.includes(e.group))
          .map((e) => ({
            id: e.id,
            ledgerId: e.ledgerId,
            ledgerName: e.ledgerName,
            group: e.group,
            voucherDate: voucher.voucherDate,
            voucherNumber: voucher.voucherNumber,
            referenceNumber: voucher.referenceNumber,
            narration: [e.remarks, voucher.narration].filter(Boolean).join(" · "),
            otherSide: otherSide(voucher),
            paid: isPaid(voucher),
            amount: Number(e.debit || 0) - Number(e.credit || 0),
          }))
      );
      // Unpaid expenses sit on payable / vendor ledgers until paid. Brand suppliers have their own Payables report.
      const [ledgers, liabilities] = [
        await api.get(`${ACCOUNTING}/ledgers`, { group: "CURRENT_LIABILITIES", includeInactive: true }),
        await api.get(`${REPORTS}/ledger-wise`, { fromDate: fyStartIso(), toDate: todayIso(), group: "CURRENT_LIABILITIES" }),
      ];
      const payableIds = new Set((ledgers || []).filter((l) => PAYABLE_GROUPS.includes(l.group) && !l.partyType && !l.systemKey).map((l) => l.id));
      setHeads(headRows.sort((a, b) => b.amount - a.amount));
      setEntries(lines);
      setPending(
        (liabilities?.ledgers || [])
          .filter((row) => payableIds.has(row.ledgerId))
          .map((row) => ({ ...row, amount: Number(row.closingCredit || 0) - Number(row.closingDebit || 0) }))
          .filter((row) => row.amount !== 0)
      );
      setApplied(period);
    } catch (error) {
      toast.error(error.message || "Failed to load the expense report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredEntries = useMemo(
    () =>
      entries.filter(
        (e) =>
          (!typeFilter || e.group === typeFilter) &&
          (ledgerFilter.length === 0 || ledgerFilter.includes(e.ledgerId)) &&
          (!monthFilter || monthOf(e.voucherDate) === monthFilter)
      ),
    [entries, typeFilter, ledgerFilter, monthFilter]
  );
  // head totals: from the ledger-wise report (complete) unless a month is picked, then from that month's entries
  const visibleHeads = useMemo(() => {
    const base = heads.filter((h) => (!typeFilter || h.group === typeFilter) && (ledgerFilter.length === 0 || ledgerFilter.includes(h.ledgerId)));
    if (!monthFilter) return base;
    return base
      .map((h) => ({ ...h, amount: sum(filteredEntries.filter((e) => e.ledgerId === h.ledgerId)) }))
      .filter((h) => h.amount !== 0);
  }, [heads, typeFilter, ledgerFilter, monthFilter, filteredEntries]);
  const months = useMemo(() => [...new Set(entries.map((e) => monthOf(e.voucherDate)))].filter(Boolean).sort(), [entries]);
  const shownMonths = useMemo(() => (monthFilter ? [monthFilter] : months), [monthFilter, months]);
  const monthWise = useMemo(
    () =>
      visibleHeads.map((h) => {
        const row = { ledgerId: h.ledgerId, ledgerName: h.ledgerName, group: h.group, total: 0 };
        shownMonths.forEach((m) => {
          row[m] = sum(filteredEntries.filter((e) => e.ledgerId === h.ledgerId && monthOf(e.voucherDate) === m));
          row.total += row[m];
        });
        return row;
      }),
    [visibleHeads, filteredEntries, shownMonths]
  );

  const entriesByHead = useMemo(() => {
    const map = {};
    filteredEntries.forEach((entry) => (map[entry.ledgerId] = map[entry.ledgerId] || []).push(entry));
    return map;
  }, [filteredEntries]);

  const toggle = (key) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const allKeys = [...EXPENSE_GROUPS, ...visibleHeads.map((h) => `head-${h.ledgerId}`)];
  const chevron = (key) => (open.has(key) ? <ChevronDown size={15} /> : <ChevronRight size={15} />);

  const total = sum(visibleHeads);
  const paidTotal = sum(filteredEntries.filter((e) => e.paid));
  const creditTotal = sum(filteredEntries.filter((e) => !e.paid));
  const filtered = Boolean(typeFilter || ledgerFilter.length || monthFilter);
  const filterLabel = [
    typeFilter ? typeLabel(typeFilter) : null,
    ledgerFilter.length ? `${ledgerFilter.length} ledger(s)` : null,
    monthFilter ? monthLabel(monthFilter) : null,
  ]
    .filter(Boolean)
    .join(", ");
  const pendingTotal = sum(pending);

  const exportExcel = () => {
    const heading = sheetHeading("Expense Report", `${periodLabel(applied)}${filterLabel ? ` — ${filterLabel}` : ""}`);
    download(`Expense report ${periodLabel(applied)}`, [
      {
        name: "By head",
        rows: [
          ...heading,
          ...tableRows(
            [
              { label: "Type", value: (row) => typeLabel(row.group) },
              { label: "Expense Head", key: "ledgerName" },
              { label: "Amount", key: "amount", format: "money" },
            ],
            [...visibleHeads].sort((a, b) => EXPENSE_GROUPS.indexOf(a.group) - EXPENSE_GROUPS.indexOf(b.group)),
            { totals: true }
          ),
        ],
        widths: [22, 36, 16],
      },
      {
        name: "Entries",
        rows: [
          ...heading,
          ...tableRows(
            [
              { label: "Date", key: "voucherDate" },
              { label: "Voucher No", key: "voucherNumber" },
              { label: "Type", value: (row) => typeLabel(row.group) },
              { label: "Expense Head", key: "ledgerName" },
              { label: "Paid From / Owed To", key: "otherSide" },
              { label: "Reference", key: "referenceNumber" },
              { label: "Narration", key: "narration" },
              { label: "Amount", key: "amount", format: "money" },
              { label: "Status", value: (row) => (row.paid ? "Paid" : "On credit") },
            ],
            filteredEntries,
            { totals: true }
          ),
        ],
        widths: [12, 24, 20, 30, 28, 20, 40, 16, 11],
      },
      {
        name: "Month wise",
        rows: [
          ...heading,
          ...tableRows(
            [
              { label: "Type", value: (row) => typeLabel(row.group) },
              { label: "Expense Head (Ledger)", key: "ledgerName" },
              ...shownMonths.map((m) => ({ label: monthLabel(m), key: m, format: "money" })),
              { label: "Total", key: "total", format: "money" },
            ],
            monthWise,
            { totals: true }
          ),
        ],
      },
      {
        name: "Pending",
        rows: [
          ...sheetHeading("Unpaid expenses pending", `As on ${todayIso()}`),
          ...tableRows(
            [
              { label: "Owed To", key: "ledgerName" },
              { label: "Group", key: "groupLabel" },
              { label: "Pending Amount", key: "amount", format: "money" },
            ],
            pending,
            { totals: true }
          ),
        ],
        widths: [36, 24, 18],
      },
    ]);
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        What the business spent in the period. Click an expense type to see its heads, and a head to see its entries. Expense heads are created
        in Ledgers and expenses are entered in Vouchers / Day Book.
      </div>
      <div className="filters-bar">
        <PeriodFilter period={period} onChange={setPeriod} />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!applied} />
      </div>
      {applied ? (
        <div className="filters-bar">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Expense type">
            <option value="">All expense types</option>
            {TYPES.map((t) => (
              <option key={t.group} value={t.group}>
                {t.label}
              </option>
            ))}
          </select>
          <MultiSelect
            options={heads.filter((h) => !typeFilter || h.group === typeFilter).map((h) => ({ value: h.ledgerId, label: h.ledgerName }))}
            selected={ledgerFilter}
            onChange={setLedgerFilter}
            placeholder="All ledgers"
          />
          <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} aria-label="Month">
            <option value="">All months</option>
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m)}
              </option>
            ))}
          </select>
          <button className={view === "head" ? "primary" : "secondary"} onClick={() => setView("head")}>
            Ledger wise
          </button>
          <button className={view === "month" ? "primary" : "secondary"} onClick={() => setView("month")}>
            Month wise
          </button>
          {filtered ? (
            <button
              className="secondary"
              onClick={() => {
                setTypeFilter("");
                setLedgerFilter([]);
                setMonthFilter("");
              }}
            >
              Clear filters
            </button>
          ) : null}
        </div>
      ) : null}

      {!applied ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        <>
          <div className="stat-grid" style={{ marginTop: "16px" }}>
            <StatCard label="Total expenses" value={`₹${money(total)}`} hint={filterLabel || periodLabel(applied)} />
            {TYPES.map((type) => (
              <StatCard key={type.group} label={type.label} value={`₹${money(sum(visibleHeads.filter((h) => h.group === type.group)))}`} hint={type.hint} />
            ))}
            <StatCard label="Paid (cash / bank)" value={`₹${money(paidTotal)}`} hint="Of the period's expenses" />
            <StatCard label="On credit" value={`₹${money(creditTotal)}`} hint="Recorded in the period without payment" />
            <StatCard label="Pending to be paid" value={`₹${money(pendingTotal)}`} hint={`As on ${todayIso()}`} tone={pendingTotal > 0 ? "bad" : undefined} />
          </div>

          {view === "month" ? (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Expense Head (Ledger)</th>
                    <th style={{ textAlign: "left" }}>Type</th>
                    {shownMonths.map((m) => (
                      <th key={m} style={cellRight}>
                        {monthLabel(m)}
                      </th>
                    ))}
                    <th style={cellRight}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {monthWise.length === 0 ? (
                    <tr>
                      <td colSpan={shownMonths.length + 3}>
                        <EmptyState>No expenses for these filters</EmptyState>
                      </td>
                    </tr>
                  ) : (
                    monthWise.map((row) => (
                      <tr key={row.ledgerId}>
                        <td style={{ textAlign: "left" }}>{row.ledgerName}</td>
                        <td style={{ textAlign: "left" }}>{typeLabel(row.group)}</td>
                        {shownMonths.map((m) => (
                          <td key={m} style={cellRight}>
                            {row[m] ? money(row[m]) : "-"}
                          </td>
                        ))}
                        <td style={{ ...cellRight, fontWeight: 700 }}>{money(row.total)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>
                      Total
                    </td>
                    {shownMonths.map((m) => (
                      <td key={m} style={{ ...cellRight, fontWeight: 700 }}>
                        {money(sum(monthWise, m))}
                      </td>
                    ))}
                    <td style={{ ...cellRight, fontWeight: 700 }}>{money(sum(monthWise, "total"))}</td>
                  </tr>
                </tfoot>
              </table>
              <div className="page-subtitle">Month-wise figures are from the expense vouchers of the period.</div>
            </div>
          ) : (
          <>
          <div className="filters-bar" style={{ marginTop: "10px" }}>
            <button className="secondary" onClick={() => setOpen(new Set(allKeys))}>
              Expand all
            </button>
            <button className="secondary" onClick={() => setOpen(new Set())}>
              Collapse all
            </button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Particulars</th>
                  <th>Date</th>
                  <th>Voucher No</th>
                  <th style={{ textAlign: "left" }}>Paid From / Owed To</th>
                  <th>Status</th>
                  <th style={cellRight}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {TYPES.filter((type) => !typeFilter || type.group === typeFilter).map((type) => {
                  const typeHeads = visibleHeads.filter((h) => h.group === type.group);
                  return (
                    <Fragment key={type.group}>
                      <tr style={{ fontWeight: 700, background: "var(--color-surface-muted, rgba(0,0,0,0.03))" }}>
                        <td style={indent(0)} colSpan={5}>
                          <button type="button" style={rowButton} onClick={() => toggle(type.group)} aria-expanded={open.has(type.group)}>
                            {chevron(type.group)}
                            {type.label}
                            <span className="page-subtitle" style={{ fontWeight: 400 }}>
                              {typeHeads.length} {typeHeads.length === 1 ? "head" : "heads"}
                            </span>
                          </button>
                        </td>
                        <td style={cellRight}>{money(sum(typeHeads))}</td>
                      </tr>
                      {open.has(type.group) && typeHeads.length === 0 ? (
                        <tr>
                          <td style={indent(1)} colSpan={6} className="page-subtitle">
                            No {type.label.toLowerCase()} in this period
                          </td>
                        </tr>
                      ) : null}
                      {open.has(type.group)
                        ? typeHeads.map((head) => {
                            const key = `head-${head.ledgerId}`;
                            const headEntries = entriesByHead[head.ledgerId] || [];
                            return (
                              <Fragment key={key}>
                                <tr style={{ fontWeight: 600 }}>
                                  <td style={indent(1)} colSpan={5}>
                                    <button type="button" style={rowButton} onClick={() => toggle(key)} aria-expanded={open.has(key)}>
                                      {chevron(key)}
                                      {head.ledgerName}
                                      <span className="page-subtitle" style={{ fontWeight: 400 }}>
                                        {headEntries.length} {headEntries.length === 1 ? "entry" : "entries"}
                                      </span>
                                    </button>
                                  </td>
                                  <td style={cellRight}>{money(head.amount)}</td>
                                </tr>
                                {open.has(key)
                                  ? headEntries.map((entry) => (
                                      <tr key={entry.id}>
                                        <td style={indent(2)} className="page-subtitle">
                                          {entry.narration || "-"}
                                        </td>
                                        <td>{entry.voucherDate}</td>
                                        <td>{entry.voucherNumber}</td>
                                        <td style={{ textAlign: "left" }}>{entry.otherSide}</td>
                                        <td>
                                          <span className={`badge ${entry.paid ? "badge-green" : "badge-amber"}`}>{entry.paid ? "Paid" : "On credit"}</span>
                                        </td>
                                        <td style={cellRight}>{money(entry.amount)}</td>
                                      </tr>
                                    ))
                                  : null}
                              </Fragment>
                            );
                          })
                        : null}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>
                    Total expenses
                  </td>
                  <td style={{ ...cellRight, fontWeight: 700 }}>{money(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          </>
          )}

          <h3 className="text-xl font-semibold" style={{ marginTop: "24px" }}>
            Unpaid expenses pending
          </h3>
          <div className="page-subtitle">
            Expenses recorded on credit stay as a liability (Salaries Payable, a garage) until paid. As on {todayIso()}; supplier bills for goods are
            in Payables &amp; Overdue.
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Owed To</th>
                  <th style={{ textAlign: "left" }}>Group</th>
                  <th style={cellRight}>Pending Amount</th>
                </tr>
              </thead>
              <tbody>
                {pending.length === 0 ? (
                  <tr>
                    <td colSpan={3}>
                      <EmptyState>Nothing is pending</EmptyState>
                    </td>
                  </tr>
                ) : (
                  pending.map((row) => (
                    <tr key={row.ledgerId}>
                      <td style={{ textAlign: "left" }}>{row.ledgerName}</td>
                      <td style={{ textAlign: "left" }}>{row.groupLabel}</td>
                      <td style={cellRight}>{row.amount < 0 ? `${money(-row.amount)} paid in advance` : money(row.amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>
                    Total pending
                  </td>
                  <td style={{ ...cellRight, fontWeight: 700 }}>{money(pendingTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ExpenseReport;
